import {
    mapArticleToCompilationArticle,
    useAddArticle,
    useCompilations,
    useCompilationsContains,
    useCreateCompilation,
    useDeleteArticles,
    isWM,
} from "@bundles/compilations"
import { useUser } from "@tm/context-distribution"
import { Article, FavoriteArticleListInfo, FavoriteArticleList } from "@tm/models"
import { useCallback, useMemo } from "react"
import { isArticleInFavoriteArticleList } from "../../../../utils/favoriteArticle"

export function useFavoriteArticleList(article: Article): FavoriteArticleList {
    const mappedArticles = mapArticleToCompilationArticle({
        articles: article ? [article] : undefined,
        wholesaler: undefined,
    })

    const { containedArticles, loading: containedArticlesLoading } = useCompilationsContains(mappedArticles, isWM())
    const { compilations, isLoading: compilationsLoading } = useCompilations(isWM())
    const { createCompilation, creatingCompilation: isCreating } = useCreateCompilation(isWM())
    const { addArticle, isLoading: addArticleLoading, compilationIdWithError } = useAddArticle(isWM())
    const { deleteArticles, isLoading: deleteArticleLoading } = useDeleteArticles(isWM())
    const { userContext } = useUser()

    const favoriteArticleListInfos: FavoriteArticleListInfo[] = useMemo(
        () =>
            compilations?.map<FavoriteArticleListInfo>((c) => {
                const containedArticle = containedArticles?.find((cA) => cA.compilationId === c.id && isArticleInFavoriteArticleList(article, cA))
                return {
                    id: c.id,
                    name: c.name,
                    selected: !!containedArticle,
                    disabled: !!containedArticle && isWM(),
                    hasErrors: compilationIdWithError === c.id,
                }
            }) ?? [],
        [compilations, containedArticles, article, compilationIdWithError]
    )

    const isInFavoriteArticleList = useMemo(() => {
        if (userContext.isAnonym) {
            const compilationId = compilations?.first()?.id
            return (
                containedArticles?.find(
                    (containedArticle) =>
                        containedArticle.compilationId === compilationId && isArticleInFavoriteArticleList(article, containedArticle)
                ) !== undefined
            )
        }
        return containedArticles?.find((containedArticle) => isArticleInFavoriteArticleList(article, containedArticle)) !== undefined
    }, [containedArticles, article, compilations, userContext])

    const toggleFavoriteArticleList = useCallback(
        (listId: string) => {
            const containedArticle = containedArticles?.find((cA) => cA.compilationId === listId && isArticleInFavoriteArticleList(article, cA))
            if (containedArticle) {
                deleteArticles({ compilationId: listId, articleIds: [containedArticle.id] })
            } else {
                addArticle({ compilationId: listId, articles: mappedArticles })
            }
        },
        [addArticle, article, containedArticles, deleteArticles, mappedArticles]
    )

    const createOrAddFavoriteArticleList = useCallback(
        (name: string) => {
            const wl = favoriteArticleListInfos.find((wl) => wl.name === name)
            if (!wl) {
                createCompilation({ name, description: "", isPublic: true }).then((response) => {
                    if (response?.compilationId) {
                        toggleFavoriteArticleList(response?.compilationId)
                    }
                })
            } else {
                toggleFavoriteArticleList(wl.id)
            }
        },
        [createCompilation, toggleFavoriteArticleList, favoriteArticleListInfos]
    )

    const createFavoriteArticleList = useCallback(
        (name: string) => {
            createCompilation({ name, description: "", isPublic: true })
        },
        [createCompilation]
    )

    const isLoading = useMemo(() => containedArticlesLoading || compilationsLoading, [compilationsLoading, containedArticlesLoading])
    const isToggling = useMemo(() => addArticleLoading || deleteArticleLoading, [addArticleLoading, deleteArticleLoading])

    return useMemo(() => {
        return {
            favoriteArticleListInfos,
            isInFavoriteArticleList,
            createFavoriteArticleList,
            toggleFavoriteArticleList,
            createOrAddFavoriteArticleList,
            isLoading,
            isToggling,
            isCreating,
            singleFavoriteArticleList: userContext.isAnonym,
        }
    }, [
        createOrAddFavoriteArticleList,
        createFavoriteArticleList,
        isCreating,
        isInFavoriteArticleList,
        isLoading,
        isToggling,
        toggleFavoriteArticleList,
        userContext.isAnonym,
        favoriteArticleListInfos,
    ])
}
